<template>
    <section class="poste-soins card" aria-label="Poste de soins infirmier">
        <div class="card-body">
            <header class="poste-head">
                <div><h3 class="c-title text-primary">Hospitalisation · poste de soins</h3>
                    <p>Installez les patients, suivez les soins hospitaliers et ambulatoires, puis préparez les sorties.</p></div>
                <div class="poste-head-actions">
                    <router-link class="poste-secondary" to="/occupation-lits">Occupation des lits</router-link>
                    <button type="button" class="poste-refresh" :disabled="loading || syncing" aria-label="Actualiser les séjours" @click="refresh">↻</button>
                </div>
            </header>
            <p v-if="error" class="poste-error" role="alert">{{ error }}</p>
            <p v-if="pendingCashierCount" class="poste-warning" role="status">{{ pendingCashierCount }} élément(s) de soins ou de chambre attendent encore leur transmission à la caisse. Le bouton d’actualisation relance l’envoi sans doublon.</p>
            <p v-if="pendingStockCount" class="poste-warning" role="status">{{ pendingStockCount }} consommable(s) attendent encore leur sortie de l’armoire de service. Actualiser relance l’écriture sans doublon.</p>
            <p v-if="unmappedStockCount" class="poste-warning" role="status">{{ unmappedStockCount }} consommation(s) ancienne(s) n’ont pas d’armoire enregistrée. Ces dossiers nécessitent un rapprochement du produit exact avec le service ; cela n’empêche pas l’installation des patients.</p>
            <div v-if="unmappedStockItems.length" class="poste-stock-repairs">
                <p v-if="stockRepairError" class="poste-error" role="alert">{{ stockRepairError }}</p>
                <div v-for="item in unmappedStockItems" :key="item.id" class="poste-stock-repair">
                    <span>{{ item.nom }} {{ item.prenoms }} · {{ item.product_label }} × {{ formatNumberCompact(item.quantity) }}</span>
                    <button type="button" class="poste-secondary" :disabled="stockRepairBusyId !== null" @click="attachConsumable(item)">Rattacher à mon service après dotation</button>
                </div>
            </div>
            <nav class="poste-tabs" aria-label="Étapes du séjour">
                <button v-for="item in tabs" :key="item.id" type="button" :class="{ active: tab === item.id }" @click="tab = item.id">
                    {{ item.label }} <span v-if="item.id !== 'care'">{{ item.id === 'cleaning' ? cleaningBeds.length : (counts[item.id] || 0) }}</span>
                </button>
            </nav>
            <div class="poste-filters">
                <input v-model.trim="search" type="search" :placeholder="tab === 'cleaning' ? 'Rechercher une chambre ou un lit' : 'Rechercher un patient, un prescripteur ou une chambre'" aria-label="Rechercher dans le poste de soins">
                <input v-if="tab !== 'care' && tab !== 'cleaning'" v-model="filterDate" type="date" aria-label="Filtrer par date de prescription">
            </div>
            <div v-if="tab === 'care'" class="poste-care">
                <SoinsAFaire ref="soinsAAdministrer" :search="search" embedded />
            </div>
            <div v-else-if="tab === 'cleaning'" class="poste-cleaning">
                <p>Un lit ne redevient disponible qu’après confirmation de son nettoyage et de sa remise en état.</p>
                <p v-if="loading && !cleaningBeds.length" role="status">Chargement des lits…</p>
                <p v-else-if="!cleaningBeds.length" class="poste-empty">Aucun lit en attente de nettoyage.</p>
                <p v-else-if="!filteredCleaningBeds.length" class="poste-empty">Aucun lit ne correspond à la recherche.</p>
                <div v-for="bed in filteredCleaningBeds" :key="bed.id" class="poste-row">
                    <strong>{{ bed.chambre || 'Chambre non renseignée' }} · {{ bed.lit }}</strong>
                    <button type="button" class="poste-secondary" :disabled="saving" @click="markReady(bed)">Confirmer lit prêt</button>
                </div>
            </div>
            <template v-else>
            <p v-if="loading && !patients.length" class="poste-empty">Chargement des séjours…</p>
            <p v-else-if="!patients.length" class="poste-empty">Aucun patient dans cette étape.</p>
            <p v-else-if="!filteredPatients.length" class="poste-empty">Aucun patient ne correspond aux filtres.</p>
            <div v-else class="poste-table-wrap">
                <table class="poste-table">
                    <thead><tr>
                        <th scope="col">Patient</th>
                        <th scope="col">Prise en charge</th>
                        <th scope="col">Prescrit par</th>
                        <th scope="col">Date / heure</th>
                        <th scope="col">{{ tab === 'planned_discharge' ? 'Sortie prévue' : 'Chambre / lit' }}</th>
                        <th scope="col">Actions</th>
                    </tr></thead>
                    <tbody>
                        <tr v-for="patient in filteredPatients" :key="patient.protocole_id">
                            <td class="poste-patient"><strong>{{ patient.nom }} {{ patient.prenoms }}</strong></td>
                            <td>{{ patient.prise_en_charge || 'Hospitalisation' }}<small v-if="patient.jours_prevus">{{ patient.jours_prevus }} jour(s) prévus</small></td>
                            <td>{{ patient.prescripteur || 'Non renseigné' }}</td>
                            <td>{{ date(patient.prescrit_le) }}</td>
                            <td>
                                <template v-if="tab === 'planned_discharge'">{{ date(patient.discharge_planned_at) }}<small v-if="patient.discharge_destination">{{ patient.discharge_destination }}</small></template>
                                <template v-else-if="patient.chambre"><span class="poste-location">Ch. {{ patient.chambre }} · lit {{ patient.lit }}</span><small v-if="patient.service_name">{{ patient.service_name }}</small><small v-if="patient.installed_at">Installé le {{ date(patient.installed_at) }}</small></template>
                                <span v-else class="poste-muted">À attribuer</span>
                                <small v-if="patient.billing_status === 'PENDING'" class="poste-warning">Facturation en attente</small>
                            </td>
                            <td><div class="poste-actions">
                                <button v-if="tab === 'pending_bed'" type="button" class="btn btn-primary-o-compact" @click="openAssignment(patient)">Attribuer un lit et installer</button>
                                <button v-if="patient.stay_id && patient.billing_status === 'PENDING'" type="button" class="poste-secondary" :disabled="saving" @click="retryBilling(patient)">Transmettre à la caisse</button>
                                <router-link v-if="tab === 'admitted'" class="poste-secondary" :to="patientLink(patient)">Ouvrir le plan</router-link>
                                <button v-if="tab === 'planned_discharge'" type="button" class="poste-primary" :disabled="saving" @click="confirmDeparture(patient)">Confirmer départ</button>
                            </div></td>
                        </tr>
                    </tbody>
                </table>
                <p class="poste-count">{{ filteredPatients.length }} patient{{ filteredPatients.length > 1 ? 's' : '' }}<span v-if="filteredPatients.length !== patients.length"> sur {{ patients.length }}</span></p>
            </div>
            </template>
        </div>

        <b-modal ref="departure" title="Confirmer le départ du patient" centered hide-footer
            :no-close-on-backdrop="saving" :no-close-on-esc="saving" :hide-header-close="saving" @hide="beforeDepartureHide">
            <template v-if="departurePatient">
                <p><strong>{{ departurePatient.nom }} {{ departurePatient.prenoms }}</strong></p>
                <p v-if="departurePatient.chambre || departurePatient.lit">{{ departurePatient.chambre }} · {{ departurePatient.lit }}</p>
                <p>Confirmez que ce patient a réellement quitté l’établissement. Le lit restera bloqué pour nettoyage.</p>
                <p v-if="departureError" class="poste-error" role="alert">{{ departureError }}</p>
                <div class="ui-modal-actions">
                    <button type="button" class="btn btn-secondary-light-o" :disabled="saving" @click="$refs.departure.hide()">Annuler</button>
                    <button type="button" class="btn btn-primary" :disabled="saving" @click="submitDeparture">{{ saving ? 'Enregistrement…' : 'Confirmer le départ' }}</button>
                </div>
            </template>
        </b-modal>
        <b-modal ref="cleaning" title="Confirmer le lit prêt" centered hide-footer
            :no-close-on-backdrop="saving" :no-close-on-esc="saving" :hide-header-close="saving" @hide="beforeCleaningHide">
            <template v-if="cleaningBed">
                <p><strong>{{ cleaningBed.chambre || 'Chambre non renseignée' }} · {{ cleaningBed.lit }}</strong></p>
                <p>Confirmez que le nettoyage et la remise en état sont terminés. Le lit sera disponible pour un nouveau patient.</p>
                <p v-if="cleaningError" class="poste-error" role="alert">{{ cleaningError }}</p>
                <div class="ui-modal-actions">
                    <button type="button" class="btn btn-secondary-light-o" :disabled="saving" @click="$refs.cleaning.hide()">Annuler</button>
                    <button type="button" class="btn btn-primary" :disabled="saving" @click="submitReady">{{ saving ? 'Enregistrement…' : 'Confirmer lit prêt' }}</button>
                </div>
            </template>
        </b-modal>

        <div v-if="selected" class="poste-overlay" @click.self="selected = null">
            <section class="poste-dialog" role="dialog" aria-modal="true" aria-label="Attribuer un lit">
                <h3>Attribuer un lit · {{ selected.nom }} {{ selected.prenoms }}</h3>
                <p>Seuls les lits libres sont proposés. L’heure d’installation est conservée dans le dossier ; les horaires des soins suivants seront calculés à partir du premier soin réellement effectué.</p>
                <label>Type de chambre
                    <select v-model="category" @change="loadBeds"><option value="all">Toutes</option><option value="shared">Commune</option><option value="private">Individuelle</option></select>
                </label>
                <label>Chambre et lit libre
                    <select v-model="bedId"><option value="">Sélectionnez un lit</option>
                        <option v-for="bed in beds" :key="bed.id" :value="bed.id">Ch. {{ bed.chambre }} · {{ bed.lit }}{{ bed.prix_chambre ? ' · ' + bed.prix_chambre + '/jour' : '' }}</option>
                    </select>
                </label>
                <p v-if="!beds.length" class="poste-warning">Aucun lit libre pour cette sélection. Vérifiez la configuration des chambres et lits.</p>
                <label v-if="assignedServices.length">Service de prise en charge
                    <select v-model="serviceId" @change="loadBeds"><option value="">Sélectionnez un service</option>
                        <option v-for="service in assignedServices" :key="service.id" :value="service.id">{{ service.name }}{{ service.stock_id ? '' : ' · armoire non configurée' }}</option>
                    </select>
                </label>
                <p v-if="serviceError" class="poste-error" role="alert">{{ serviceError }}</p>
                <p v-else-if="!serviceLoading && !assignedServices.length" class="poste-warning">Aucun service d’affectation configuré : le séjour ne pourra pas encore être imputé à une armoire.</p>
                <label>Installation effective <input v-model="installedAt" type="datetime-local"></label>
                <p v-if="modalError" class="poste-error" role="alert">{{ modalError }}</p>
                <div class="poste-dialog-actions"><button type="button" class="poste-secondary" @click="selected = null">Annuler</button>
                    <button type="button" class="poste-primary" :disabled="saving || serviceLoading || serviceError || !bedId || !installedAt || (assignedServices.length && !serviceId)" @click="assign">{{ saving ? 'Installation…' : 'Confirmer l’installation' }}</button></div>
            </section>
        </div>
    </section>
</template>

<script>
import stationApi from '@/api/consultations/_nursing-station'
import { parseBackendTimestamp } from '@/utils/backend-timestamp'
import protocoleApi from '@/api/consultations/_protocole-soins'
import finance from '@/api/finances/_finance'
import SoinsAFaire from '../soins-a-faire'

export default {
    name: 'poste-soins-infirmier',
    components: { SoinsAFaire },
    data() {
        return {
            tabs: [
                { id: 'pending_bed', label: 'En attente de lit' },
                { id: 'admitted', label: 'Hospitalisés / soins en cours' },
                { id: 'care', label: 'Soins à administrer' },
                { id: 'planned_discharge', label: 'Sorties prévues' },
                { id: 'cleaning', label: 'Lits en nettoyage' }
            ],
            tab: 'pending_bed', lists: {}, counts: {}, cleaningBeds: [], pendingCashierCount: 0,
            pendingStockCount: 0, unmappedStockCount: 0, unmappedStockItems: [],
            stockRepairBusyId: null, stockRepairError: '', loading: false, saving: false, syncing: false,
            search: '', filterDate: '',
            error: '', modalError: '', selected: null, beds: [], bedId: '', category: 'all', installedAt: '',
            departurePatient: null, departureError: '', cleaningBed: null, cleaningError: '',
            assignedServices: [], serviceId: '', serviceLoading: false, serviceError: ''
        }
    },
    computed: {
        filteredCleaningBeds() {
            const search = this.search.toLocaleLowerCase('fr').trim()
            return this.cleaningBeds.filter(bed => !search || [bed.chambre, bed.lit].filter(Boolean).join(' ').toLocaleLowerCase('fr').includes(search))
        },
        patients() { return this.lists[this.tab] || [] },
        filteredPatients() {
            const search = this.search.toLocaleLowerCase('fr').trim()
            return this.patients.filter(item => {
                const text = [item.nom, item.prenoms, item.prescripteur, item.prise_en_charge, item.chambre, item.lit]
                    .filter(Boolean).join(' ').toLocaleLowerCase('fr')
                return (!search || text.includes(search)) && (!this.filterDate || String(item.prescrit_le || '').slice(0, 10) === this.filterDate)
            })
        }
    },
    mounted() { this.refresh() },
    methods: {
        date(value) {
            if (!value) return '—'
            const date = parseBackendTimestamp(value)
            return isNaN(date.getTime()) ? '—' : date.toLocaleString('fr-FR', { timeZone: this.getClinicTimezone() })
        },
        patientLink(item) {
            return { name: 'patientInfo', params: { id: item.patient_id, consultation: 'consulter' },
                query: { inf001: item.consultation_id, tarif: item.tarif_consultation_id, suivi: 'traitement' } }
        },
        async load() {
            this.loading = true; this.error = ''
            try {
                const [result, cleaning] = await Promise.all([stationApi.pipeline(), stationApi.cleaningBeds()])
                if (!result || !result.success) throw new Error('Séjours indisponibles.')
                this.lists = result.data || {}; this.counts = result.counts || {}
                this.cleaningBeds = cleaning.data || []
            } catch (error) { this.error = 'Impossible de charger les séjours. Réessayez.' }
            finally { this.loading = false }
        },
        async refresh() {
            await this.load()
            if (!this.error) await this.reconcilePendingBilling()
            await this.reconcilePendingStock()
            if (this.$refs.soinsAAdministrer) await this.$refs.soinsAAdministrer.charger(1)
        },
        async reconcilePendingStock() {
            try {
                const result = await stationApi.reconcileStock()
                this.pendingStockCount = result.pending_count || 0
                this.unmappedStockCount = result.unmapped_count || 0
                this.unmappedStockItems = result.unmapped_items || []
            } catch (error) { this.error = 'État des consommables du service indisponible. Réessayez.' }
        },
        async attachConsumable(item) {
            if (this.stockRepairBusyId !== null) return
            this.stockRepairError = ''
            try {
                const result = await stationApi.myServices()
                const services = result && result.data || []
                const service = services.find(row => row.is_primary) || (services.length === 1 ? services[0] : null)
                if (!service || !service.stock_id) throw new Error('Aucune armoire principale affectée à votre compte.')
                if (!window.confirm(`Confirmer que ${item.product_label} (article ${item.product_id}) a été doté dans l’armoire ${service.name} pour le soin de ${item.nom} ${item.prenoms} ?`)) return
                this.stockRepairBusyId = item.id
                const attached = await stationApi.attachConsumableService(item.id, service.id)
                await this.reconcilePendingStock()
                if (attached.stock_status !== 'DONE')
                    this.stockRepairError = 'Le rattachement est enregistré, mais la sortie de stock reste en attente. Actualisez après vérification du service de stock.'
            } catch (error) {
                this.stockRepairError = error.response && error.response.status === 409
                    ? 'L’article exact et sa quantité ne sont pas encore disponibles dans l’armoire de ce service. Effectuez la dotation depuis la Pharmacie, puis réessayez.'
                    : 'Rapprochement du stock indisponible. Vérifiez votre affectation et réessayez.'
            } finally { this.stockRepairBusyId = null }
        },
        async reconcilePendingBilling() {
            if (this.syncing) return
            this.syncing = true
            let failures = 0
            let statusUnavailable = false
            try {
                for (let batch = 0; batch < 3; batch++) {
                    const pending = await stationApi.pendingCashierItems()
                    const items = pending.data || {}
                    this.pendingCashierCount = pending.pending_count || 0
                    if (!this.pendingCashierCount) break
                    let confirmed = 0
                    for (const stayId of items.rooms || []) {
                        try {
                            const result = await stationApi.pendingBilling(stayId)
                            await this.sendBilling(stayId, result.billing)
                            confirmed++
                        } catch (error) { failures++ }
                    }
                    const grouped = new Map()
                    for (const item of items.consumables || [])
                        grouped.set(`${item.care_type}:${item.care_item_id}`, item)
                    for (const item of grouped.values()) {
                        try {
                            const result = await protocoleApi.lireFacturationConsommablesSoin({
                                care_item_id: item.care_item_id, type: item.care_type
                            })
                            for (const operation of result.finance_ops || []) {
                                try {
                                    const sent = await finance.storeLigneOperation(operation, { timeout: 12000 })
                                    if (!sent || !sent.success) throw new Error('Caisse indisponible')
                                    await protocoleApi.confirmerFacturationConsommable(operation.source_id)
                                    confirmed++
                                } catch (error) { failures++ }
                            }
                        } catch (error) { failures++ }
                    }
                    if (!confirmed || !pending.has_more) break
                }
                const remaining = await stationApi.pendingCashierItems()
                this.pendingCashierCount = remaining.pending_count || 0
            } catch (error) {
                failures++
                statusUnavailable = true
            }
            this.syncing = false
            await this.load()
            if (statusUnavailable)
                this.error = 'État de transmission vers la caisse indisponible. Actualisez pour réessayer.'
            else if (failures || this.pendingCashierCount)
                this.error = `${this.pendingCashierCount} élément(s) restent à transmettre à la caisse. Réessayez après vérification de la connexion.`
        },
        async loadBeds() {
            this.bedId = ''; this.modalError = ''
            // Lits des chambres de l'unité choisie, et des chambres rattachées à aucune unité.
            try { const result = await stationApi.availableBeds(this.category, this.serviceId); this.beds = result.data || [] }
            catch (error) { this.beds = []; this.modalError = 'Impossible de charger les lits libres.' }
        },
        openAssignment(patient) {
            this.selected = patient; this.category = 'all'; this.bedId = ''; this.modalError = ''
            this.assignedServices = []; this.serviceId = ''; this.serviceError = ''; this.loadAssignedServices()
            const now = new Date(); now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
            this.installedAt = now.toISOString().slice(0, 16)
            this.loadBeds()
        },
        async loadAssignedServices() {
            this.serviceLoading = true; this.serviceError = ''
            try {
                const result = await stationApi.myServices()
                if (!result || !result.success) throw new Error('Affectations indisponibles')
                this.assignedServices = result.data || []
                const preferred = this.assignedServices.find(item => item.is_primary) || this.assignedServices[0]
                this.serviceId = preferred ? preferred.id : ''
                if (this.serviceId) this.loadBeds()
            } catch (error) {
                this.assignedServices = []; this.serviceError = 'Impossible de vérifier vos services d’affectation. Réessayez.'
            } finally { this.serviceLoading = false }
        },
        async sendBilling(stayId, operation) {
            if (!operation) throw new Error('Opération de chambre introuvable.')
            const result = await finance.storeLigneOperation(operation, { timeout: 12000 })
            if (!result || !result.success) throw new Error('Transmission à la caisse non confirmée.')
            await stationApi.confirmBilling(stayId)
        },
        async assign() {
            if (!this.selected || !this.bedId || !this.installedAt || this.saving) return
            this.saving = true; this.modalError = ''
            try {
                // Recette du 03/10 : l'heure saisie est celle du poste ; elle part avec son fuseau (ISO 8601 UTC),
                // sinon un poste réglé sur un autre fuseau que le serveur voit l'installation « dans le futur ».
                const instant = new Date(this.installedAt)
                if (isNaN(instant.getTime())) throw new Error('Date d’installation invalide.')
                const result = await stationApi.assignBed(this.selected.protocole_id, {
                    bed_id: Number(this.bedId), installed_at: instant.toISOString(),
                    service_id: this.serviceId ? Number(this.serviceId) : null
                })
                if (!result || !result.success) throw new Error('Lit non attribué.')
                this.selected = null; this.tab = 'admitted'
                await this.load()
                try {
                    if (result.billing_error)
                        throw new Error('Facturation de la chambre en attente.')
                    if (result.billing) await this.sendBilling(result.stay_id, result.billing)
                    await this.load()
                }
                catch (error) { this.error = 'Le lit est attribué, mais la transmission à la caisse est à réessayer depuis la fiche du séjour.' }
            } catch (error) {
                await this.loadBeds()
                this.modalError = (error.response && error.response.data && error.response.data.message) || error.message || 'Attribution impossible.'
            } finally { this.saving = false }
        },
        async retryBilling(patient) {
            this.saving = true; this.error = ''
            try {
                const result = await stationApi.pendingBilling(patient.stay_id)
                await this.sendBilling(patient.stay_id, result.billing)
                await this.load()
            } catch (error) { this.error = 'La caisse n’a pas confirmé la chambre. Réessayez ; aucun doublon ne sera créé.' }
            finally { this.saving = false }
        },
        confirmDeparture(patient) {
            if (this.saving || !patient || !patient.stay_id) return
            this.departurePatient = Object.assign({}, patient); this.departureError = ''
            this.$refs.departure.show()
        },
        beforeDepartureHide(event) {
            if (this.saving) { event.preventDefault(); return }
            this.departurePatient = null
        },
        async submitDeparture() {
            if (this.saving || !this.departurePatient) return
            const stayId = this.departurePatient.stay_id
            this.saving = true; this.departureError = ''
            try {
                const result = await stationApi.discharge(stayId)
                if (!result || result.success !== true) throw new Error('Départ non confirmé')
                if (this._isDestroyed) return
                this.saving = false; this.$refs.departure.hide(); this.departurePatient = null
                await this.load()
            }
            catch (error) { if (!this._isDestroyed) this.departureError = (error.response && error.response.data && error.response.data.message) || 'Départ non confirmé. Vérifiez l’autorisation médicale.' }
            finally { this.saving = false }
        },
        markReady(bed) {
            if (this.saving || !bed || !bed.id) return
            this.cleaningBed = Object.assign({}, bed); this.cleaningError = ''
            this.$refs.cleaning.show()
        },
        beforeCleaningHide(event) {
            if (this.saving) { event.preventDefault(); return }
            this.cleaningBed = null
        },
        async submitReady() {
            if (this.saving || !this.cleaningBed) return
            const bedId = this.cleaningBed.id
            this.saving = true; this.cleaningError = ''
            try {
                const result = await stationApi.markBedReady(bedId)
                if (!result || result.success !== true) throw new Error('Remise en état non confirmée')
                if (this._isDestroyed) return
                this.saving = false; this.$refs.cleaning.hide(); this.cleaningBed = null
                await this.load()
            }
            catch (error) { if (!this._isDestroyed) this.cleaningError = (error.response && error.response.data && error.response.data.message) || 'Le lit reste indisponible. Vérifiez son état puis réessayez.' }
            finally { this.saving = false }
        }
    }
}
</script>

<style scoped lang="scss">
.poste-soins { margin-bottom: 1.5rem; border: 0; border-radius: 22px; box-shadow: none; }
.poste-soins .card-body { padding: 26px 30px; }
.poste-head, .poste-row, .poste-dialog-actions { display: flex; justify-content: space-between; gap: 16px; }
.poste-head-actions { display: flex; align-items: center; gap: 10px; }
.poste-head h3 { margin: 0 0 5px; }
.poste-head p, .poste-dialog p { color: var(--ui-text-secondary, #71809d); margin: 0; }
.poste-refresh { width: 38px; height: 38px; border: 1px solid #d7e2f9; border-radius: 10px; background: #f6f9ff; color: #3d70ed; font-size: 1.5rem; }
.poste-tabs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 22px; border-bottom: 1px solid #dce5f5; }
.poste-tabs button { background: none; border: 0; border-bottom: 3px solid transparent; padding: 10px 13px; color: var(--ui-text-secondary, #697a98); font-weight: 600; }
.poste-tabs button.active { border-bottom-color: #4478f5; color: #3468e6; }
.poste-tabs span { display: inline-block; background: #ebf1ff; border-radius: 12px; padding: 1px 7px; }
.poste-filters { display: flex; gap: 12px; margin-top: 16px; }
.poste-filters input { min-height: 42px; border: 1px solid #b9c9e7; border-radius: 10px; padding: 9px 13px; color: var(--ui-text, #344465); background: white; }
.poste-filters input[type="search"] { flex: 1; min-width: 180px; }
.poste-filters input[type="date"] { width: 170px; }
.poste-empty { padding-top: 18px; color: var(--ui-text-secondary, #71809d); }
.poste-table-wrap { overflow-x: auto; margin-top: 16px; }
.poste-table { width: 100%; border-collapse: collapse; color: var(--ui-text, #344465); }
.poste-table th { background: #f2f5fb; color: var(--ui-text, #596985); font-size: .86rem; font-weight: 700; text-align: left; white-space: nowrap; }
.poste-table th, .poste-table td { padding: 12px 14px; border-bottom: 1px solid #e5ebf5; vertical-align: middle; text-align: left !important; }
.poste-table tbody tr:hover { background: #e8f1ff; }
.poste-table td small { display: block; color: var(--ui-text-secondary, #73829d); margin-top: 3px; }
.poste-table .poste-patient { min-width: 190px; }
.poste-table .poste-patient strong { font-weight: 700; }
.poste-table td:last-child { min-width: 210px; }
.poste-muted, .poste-count { color: var(--ui-text-secondary, #73829d); }
.poste-count { margin: 12px 0 0; font-size: .86rem; }
.poste-care { padding-top: 16px; }
.poste-row { align-items: center; padding: 15px 3px; border-bottom: 1px solid #e5ebf5; }
.poste-main { display: flex; flex-direction: column; gap: 2px; color: var(--ui-text, #344465); }
.poste-main small { color: var(--ui-text-secondary, #73829d); }
.poste-location { color: #2b6e53; font-weight: 700; }
.poste-cleaning { margin-top: 22px; padding-top: 14px; border-top: 1px solid #e5ebf5; }
.poste-cleaning h4 { margin: 0 0 5px; color: var(--ui-text, #344465); font-size: 1rem; }
.poste-cleaning p { margin: 0 0 6px; color: var(--ui-text-secondary, #71809d); }
.poste-actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-start; gap: 8px; }
.poste-primary, .poste-secondary { display: inline-block; padding: 9px 13px; border-radius: 9px; font-weight: 650; cursor: pointer; }
.poste-primary { border: 1px solid #3b73f2; background: #3b73f2; color: white; }
.poste-secondary { border: 1px solid #bdcff6; background: white; color: #3468e6; }
.poste-error { color: #a94945; }
.poste-warning { color: #9c671e !important; }
.poste-stock-repairs { border: 1px solid #e9d8ad; border-radius: 10px; background: #fffaf0; padding: 8px 12px; }
.poste-stock-repair { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 7px 0; }
.poste-stock-repair + .poste-stock-repair { border-top: 1px solid #f0e3c6; }
.poste-stock-repair span { color: #605137; }
.poste-stock-repair button { flex-shrink: 0; }
.poste-overlay { position: fixed; z-index: 3000; inset: 0; background: rgba(28, 39, 65, .55); display: grid; place-items: center; padding: 20px; }
.poste-dialog { width: 100%; max-width: 560px; box-sizing: border-box; background: white; border-radius: 17px; padding: 24px; box-shadow: 0 20px 70px rgba(22, 38, 72, .25); }
.poste-dialog h3 { color: var(--ui-text, #344465); margin: 0 0 7px; }
.poste-dialog label { display: block; margin-top: 16px; color: var(--ui-text, #344465); font-weight: 650; }
.poste-dialog select, .poste-dialog input { display: block; width: 100%; margin-top: 6px; padding: 10px; border: 1px solid #b8c9e6; border-radius: 8px; }
.poste-dialog-actions { justify-content: flex-end; margin-top: 22px; }
@media (max-width: 700px) { .poste-row, .poste-stock-repair { align-items: flex-start; flex-direction: column; } .poste-filters { flex-direction: column; } .poste-filters input[type="date"] { width: 100%; } .poste-table { min-width: 900px; } }
</style>
